// @flow
import React from 'react';
import Debug from 'debug';
import { FormattedMessage } from 'react-intl';
import makeStyles from '@material-ui/core/styles/makeStyles';
import type { Config } from '../../../types';
import { TYPE } from '../../../constants/targetConstants';
import ConfigItem from '../../Options/Config/ConfigItem';
import Section from '../../Options/Config/Section';
import DoubleInput from '../../Options/Config/ConfigItem/DoubleInput';

const debug = Debug('amms:modules:tisPlayer:generator:item');

const parseSteps = (value) => {
    let steps;
    if(value) {
        steps = [];

        for(let i = 0, length = value.length; i < length; i += 1) {
            const step = {};
            const x = parseFloat(value[i].x);
            const y = parseFloat(value[i].y);

            if(!isNaN(x)) {
                step.x = x;
            }
            if(!isNaN(y)) {
                step.y = y;
            }

            steps.push(step);
        }
    }

    return steps;
};

const inputWidth = 160;
const useStyles = makeStyles({
    generatorItem: {
        border: '1px solid gray',
        display: 'inline-block'
    },
    input: {
        width: inputWidth,
        display: 'inline-block',
        '&:first-child': {
            marginRight: 10
        }
    },
    field: {
        minWidth: inputWidth,
        width: inputWidth
    }
});

type Item = $ReadOnly<{

}>;

type Props = $ReadOnly<{
    item: Item,
    path: $ReadOnlyArray<string>,

    onChange?: (path: $ReadOnlyArray<string>, value: any, config: Config) => void
}>;
function GeneratorItem({ item, path, onChange }: Props) {
    const classes = useStyles();

    return (
        <div className={classes.generatorItem}>
            <div>
                <ConfigItem
                    className={classes.input}
                    type="boolean"
                    label={(
                        <FormattedMessage
                            id="tisPlayer.generator.item.enabled"
                            defaultMessage="enabled"
                        />
                    )}
                    path={[...path, 'enabled']}
                    size="small"
                    onChange={onChange}
                />
                <ConfigItem
                    className={classes.input}
                    type="select"
                    label={(
                        <FormattedMessage
                            id="tisPlayer.generator.item.type"
                            defaultMessage="Type"
                        />
                    )}
                    defaultValue={TYPE.car}
                    path={[...path, 'type']}
                    items={[
                        {
                            label: TYPE.car,
                            value: TYPE.car
                        },
                        {
                            label: TYPE.plane,
                            value: TYPE.plane
                        }
                    ]}
                    size="small"
                    onChange={onChange}
                />
            </div>

            <div>
                <ConfigItem
                    className={classes.input}
                    classes={{ field: classes.field }}
                    type="text"
                    label={(
                        <FormattedMessage
                            id="tisPlayer.generator.item.icao"
                            defaultMessage="ICAO"
                        />
                    )}
                    path={[...path, 'icao']}
                    size="small"
                    onChange={onChange}
                />
                <ConfigItem
                    className={classes.input}
                    classes={{ field: classes.field }}
                    type="text"
                    label={(
                        <FormattedMessage
                            id="tisPlayer.generator.item.callsign"
                            defaultMessage="Callsign"
                        />
                    )}
                    path={[...path, 'callsign']}
                    size="small"
                    onChange={onChange}
                />
            </div>

            <Section
                title={(<FormattedMessage
                    id="tisPlayer.generator.coordinates"
                    defaultMessage="Coordinates"
                />)}
            >
                <ConfigItem
                    type="select"
                    label={(
                        <FormattedMessage
                            id="tisPlayer.generator.item.generatorType"
                            defaultMessage="Generator type"
                        />
                    )}
                    path={[...path, 'generatorType']}
                    items={[
                        {
                            label: 'Absolute',
                            value: 'absolute'
                        },
                        {
                            label: 'Relative',
                            value: 'relative'
                        }
                    ]}
                    size="small"
                    onChange={onChange}
                />

                <div>
                    <ConfigItem
                        className={classes.input}
                        classes={{ field: classes.field }}
                        type="number"
                        label={(
                            <FormattedMessage
                                id="tisPlayer.generator.item.interval"
                                defaultMessage="Interval"
                            />
                        )}
                        path={[...path, 'interval']}
                        size="small"
                        onChange={onChange}
                    />
                    <ConfigItem
                        className={classes.input}
                        classes={{ field: classes.field }}
                        type="number"
                        label={(
                            <FormattedMessage
                                id="tisPlayer.generator.item.delay"
                                defaultMessage="Delay"
                            />
                        )}
                        path={[...path, 'delay']}
                        size="small"
                        onChange={onChange}
                    />
                </div>

                {item.generatorType === 'relative' &&
                    <React.Fragment>
                        <ConfigItem
                            type="coordinate"
                            label={(
                                <FormattedMessage
                                    id="tisPlayer.generator.item.baseCoordinates"
                                    defaultMessage="Base Coordinates"
                                />
                            )}
                            path={[...path, 'baseCoordinates']}
                            size="small"
                            onChange={onChange}
                        />
                        <ConfigItem
                            type="number"
                            label={(
                                <FormattedMessage
                                    id="tisPlayer.generator.item.stepMultiplier"
                                    defaultMessage="Multiplier"
                                />
                            )}
                            path={[...path, 'stepMultiplier']}
                            size="small"
                            onChange={onChange}
                        />
                    </React.Fragment>
                }
                <ConfigItem
                    type="array"
                    label={(
                        <FormattedMessage
                            id="tisPlayer.generator.item.steps"
                            defaultMessage="Steps"
                        />
                    )}
                    path={[...path, 'steps']}
                    expanded={false}
                    editable
                    sortable
                    size="small"
                    inputComponent={DoubleInput}
                    parseLabel={(value) => value && `${value.x}, ${value.y}`}
                    outputFormatter={parseSteps}
                    onChange={onChange}
                />
            </Section>
        </div>
    );
}

export default GeneratorItem;